import type { UnitSnapshot } from "./timesheet-days";

/**
 * «Как вчера» (R10, FR-017): чистые функции поиска дня-источника и подписи кнопки.
 * Даты — строки `ГГГГ-ММ-ДД`; день из прошлого месяца отдаёт `lookup` (месяцы в сторе и копии).
 */

/** Сколько дней назад искать источник */
export const COPY_SEARCH_DAYS = 62;

const WEEKDAYS_SHORT = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function parse(date: string): number {
    const [year, month, day] = date.split("-").map(Number);

    return Date.UTC(year, month - 1, day);
}

function format(ms: number): string {
    return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Последний более ранний день, в котором есть запись рабочего типа (`isWorkedOut`).
 * `lookup` отдаёт записи дня (с учётом неотправленных версий) или `undefined`, если дня нет на
 * устройстве. Ничего не нашлось за `maxDaysBack` дней — `null`.
 */
export function findCopySource(
    target: string,
    lookup: (date: string) => { units: readonly UnitSnapshot[] } | undefined,
    maxDaysBack = COPY_SEARCH_DAYS
): string | null {
    const start = parse(target);

    for (let back = 1; back <= maxDaysBack; back++) {
        const date = format(start - back * MS_PER_DAY);

        if (lookup(date)?.units.some((unit) => unit.isWorkedOut)) {
            return date;
        }
    }

    return null;
}

/** Записи, которые копируются: рабочие типы, без комментария записи */
export function copyUnits(units: readonly UnitSnapshot[]): UnitSnapshot[] {
    return units.filter((unit) => unit.isWorkedOut).map((unit) => ({ ...unit, comment: "" }));
}

/** Короткий номер проекта: имя проекта до первого пробела, иначе полный инвентарный номер */
function projectName(unit: UnitSnapshot): string {
    const name = (unit.order as { name?: string } | null | undefined)?.name?.trim();

    if (name) {
        return name.split(/\s+/)[0];
    }

    return String(unit.fullInventoryNumber ?? "");
}

/** «8 ч», «7:30 ч» */
function duration(unit: UnitSnapshot): string {
    return unit.minutes > 0 ? `${unit.hours}:${String(unit.minutes).padStart(2, "0")} ч` : `${unit.hours} ч`;
}

function formatSource(source: string): string {
    const date = new Date(parse(source));

    return `в ${WEEKDAYS_SHORT[date.getUTCDay()]}, ${date.getUTCDate()} ${MONTHS_SHORT[date.getUTCMonth()]}`;
}

/** «Как вчера: Р-217 · 8 ч» / «Как в пн, 12 окт: Р-217 · 6 ч, Р-301 · 2 ч» */
export function copyLabel(source: string, target: string, units: readonly UnitSnapshot[]): string {
    const yesterday = parse(target) - parse(source) === MS_PER_DAY;
    const when = yesterday ? "вчера" : formatSource(source);
    const body = copyUnits(units)
        .map((unit) => {
            const project = projectName(unit);

            return project ? `${project} · ${duration(unit)}` : duration(unit);
        })
        .join(", ");

    return body ? `Как ${when}: ${body}` : `Как ${when}`;
}
